import { isTrue } from '@avantia/client-and-server-utilities';
import { HpError } from '@avantia/logging-and-errors';
import { createControlId, HpCheckBoxTextAndStyle } from '@avantia/questionset-model';
import { SvgIcon } from '@avantia/style-guide';

import { HpInputControlProps } from '../../../types/inputControlTypes';
import { HpClientStandardInputTextAndStyle } from '../../../types/inputTypesClient';
import { HtmlWidget } from '../../widgets/htmlWidget';
import { StandardFormGroupWidget } from '../../widgets/standardFormGroupWidget';
import { createSpaceKeyAsClickHandler } from '../controlEvents';
import { createStandardMouseEvent } from '../inputControl';

export function CheckBoxControl({
  answerSource,
  data,
  dataType,
  disabled,
  errors,
  formatType,
  id,
  name,
  onChange,
  onClick,
  question,
  questionText,
  subtext,
  ui,
  value
}: HpInputControlProps): JSX.Element {
  if (dataType !== 'boolean') {
    throw new HpError('The only valid dataType for CheckBoxControl is boolean.');
  }

  const textAndStyle = data.textAndStyle[formatType] as HpClientStandardInputTextAndStyle;
  const { checkBoxLabelText } = textAndStyle as HpCheckBoxTextAndStyle;
  const error = errors ? errors[id] : undefined;
  const isChecked = isTrue(value as any);
  const onClickFunc = disabled ? undefined : createStandardMouseEvent(onClick, question);
  const onKeyPressFunc = onClickFunc && createSpaceKeyAsClickHandler(onClickFunc);
  const optionText = checkBoxLabelText || questionText;

  return (
    <StandardFormGroupWidget
      id={id}
      question={question}
      questionText={questionText !== optionText ? questionText : undefined}
      checkBoxLabelText={checkBoxLabelText}
      control={
        <CheckBoxItem
          disabled={disabled}
          id={id}
          isChecked={isChecked}
          itemKey={createControlId({ questionId: id, key: isChecked ? 'false' : 'true' })}
          name={name}
          onClick={onClickFunc}
          onKeyPress={onKeyPressFunc}
          optionText={optionText}
        />
      }
      textAndStyle={textAndStyle}
      error={error}
      answerSource={answerSource}
      controlType="checkbox-list"
      dataType={dataType}
      helpInfo={data.helpInfo}
      helpInfoUI={ui.helpInfo}
      subtext={subtext}
      onClick={onChange}
    />
  );
}

export interface HpCheckBoxItemProps {
  disabled?: boolean;
  id: string;
  isChecked: boolean;
  itemKey: string;
  name: string | undefined;
  onClick: ((event: React.MouseEvent<HTMLElement>) => void) | undefined;
  onKeyPress: React.KeyboardEventHandler<Element> | undefined;
  optionText: string;
}

export function CheckBoxItem({
  disabled,
  id,
  isChecked,
  itemKey,
  name,
  onClick,
  onKeyPress,
  optionText
}: HpCheckBoxItemProps): JSX.Element {
  optionText = optionText || '';

  // 'length < 30' is for optimisation
  const isSingleWordOptionText = optionText.length < 30 && optionText.indexOf(' ') < 0 && optionText.indexOf('<') < 0;

  // Remove flicker on the quote-page (i.e. 'Included' for buildings/contents AD sections).
  const className = isSingleWordOptionText ? 'hp-single-line-alignment' : undefined;
  return (
    <div className="form-check hp-checkbox-list" key={itemKey}>
      <input
        className={isChecked ? 'hp-checked' : ''}
        disabled={disabled === true}
        id={itemKey}
        name={name || id}
        onClick={onClick}
        type="checkbox"
      />
      <label
        className="form-check-label"
        htmlFor={itemKey}
        tabIndex={0}
        onKeyPress={onKeyPress}
        id={itemKey}
        onClick={onClick}
      >
        {isChecked && <SvgIcon type="check" size="small" />}
        <HtmlWidget className={className} html={optionText} tagName="span" />
      </label>
    </div>
  );
}
